import StepTitle from 'components/pages/OnboardingPage/components/StepTitle';
import {IDefaultStepProps} from 'components/pages/OnboardingPage/useOnboarding';
import styles from './Steps.module.scss';
import useModal from 'components/ui/ModalContainer/useModal';
import OnboardingOverlay from 'components/ui/Overlays/OnboardingOverlay/OnboardingOverlay';
import {ConfirmationModal} from 'components/modals';
import {useAuth} from 'context/auth-context';
import CrpTable from 'components/pages/OnboardingPage/components/CrpTable';
import NextStepButton from 'components/pages/OnboardingPage/components/NextStepButton';
import {useQueryClient} from '@tanstack/react-query';
import CrpForm from 'components/pages/OnboardingPage/components/CrpForm';
import useCrpTable from 'components/pages/OnboardingPage/components/hooks/useCrpTable';
import ConfirmationOverlay from 'components/ui/Overlays/ConfirmationOverlay/ConfirmationOverlay';
import {CrpVariants} from 'constants/businessPartnerAttributes';
import {USER_CACHE} from 'constants/cache';
import {LEGAL_FORM_SINGLE_PERSON_ENTERPRISE} from 'constants/common';
import {
    crpFormValidationSchema,
    crpFormBaseValidationSchema,
} from 'form/validation/businessPartner';
import CrpInfo from 'components/pages/OnboardingPage/components/Infos/CrpInfo';

const Crp = (props: IDefaultStepProps) => {
    const {
        isIndividual,
        activeBusinessPartner,
        isCrpLoading,
        crpList,
        setActiveBusinessPartner,
        onSuccess,
        currentStep,
        legalType,
        inReview,
        isCrpFetched,
        areAllRolesFilled,
    } = props;
    const queryClient = useQueryClient();

    const {openModalWithContent, closeModal} = useModal();
    const {user} = useAuth();

    const {handleCrpEdit} = useCrpTable({crpList, currentStep});

    // special logic for this page, single person enterprise needs the same form here as private persons
    const isSinglePerson =
        isIndividual ||
        activeBusinessPartner.legalForm === LEGAL_FORM_SINGLE_PERSON_ENTERPRISE;

    const checkAllCrpData = (formOfControl?: string): boolean => {
        let result = true;
        crpList.forEach(crpBp => {
            const roles: CrpVariants[] = [];
            if (crpBp.isOwner) roles.push(CrpVariants.OWNER);
            if (crpBp.admin) roles.push(CrpVariants.ADMIN);
            if (crpBp.hasSigningRights) roles.push(CrpVariants.SIGNER);
            if (crpBp.contractSigner) roles.push(CrpVariants.CONTRACT_SIGNER);

            try {
                crpFormValidationSchema(
                    roles,
                    crpBp.confidential,
                    legalType,
                    formOfControl?.toString(),
                ).validateSync({...crpBp.crp, ...crpBp});
            } catch (err) {
                result = false;
                console.log({err, crpBp});
                openModalWithContent(
                    'crpForm',
                    <OnboardingOverlay fullHeight>
                        <CrpForm
                            legalType={legalType}
                            handleSubmit={(crpValues, setCrpError) => {
                                handleCrpEdit(
                                    crpValues,
                                    roles,
                                    crpBp,
                                    setCrpError,
                                );
                            }}
                            crpBpId={crpBp['@id']}
                            formOfControl={formOfControl?.toString()}
                            inReview={inReview}
                            crpList={crpList}
                        />
                    </OnboardingOverlay>,
                );
            }
        });

        return result;
    };

    const handleFormSubmit = () => {
        const allRolesFilled = areAllRolesFilled(crpList);
        if (allRolesFilled !== true) {
            return openModalWithContent(
                'confirmation',
                <OnboardingOverlay hideCloseButton>
                    <ConfirmationModal
                        title="Warning"
                        description={allRolesFilled}
                        onSubmit={() => {
                            closeModal('confirmation');
                        }}
                    />
                </OnboardingOverlay>,
            );
        }

        if (
            !checkAllCrpData(
                activeBusinessPartner.gwgInfo?.formOfControl?.toString(),
            )
        ) {
            return;
        }

        if (isIndividual) {
            const allRoles = [
                CrpVariants.ADMIN,
                CrpVariants.CONTRACT_SIGNER,
                CrpVariants.OWNER,
                CrpVariants.SIGNER,
            ];
            const userCrp = crpList?.find(
                crpBp => crpBp.crp.user === user['@id'],
            );
            if (!userCrp) return;
            try {
                crpFormBaseValidationSchema(allRoles, false).validateSync({
                    ...userCrp.crp,
                    ...userCrp,
                });
            } catch (error) {
                openModalWithContent(
                    'crpForm',
                    <OnboardingOverlay fullHeight>
                        <CrpForm
                            legalType={legalType}
                            handleSubmit={(crpValues, setCrpError) => {
                                handleCrpEdit(
                                    crpValues,
                                    allRoles,
                                    userCrp,
                                    setCrpError,
                                );
                            }}
                            crpBpId={userCrp['@id']}
                            formOfControl={activeBusinessPartner.gwgInfo?.formOfControl?.toString()}
                            inReview={inReview}
                            crpList={crpList}
                        />
                    </OnboardingOverlay>,
                );
                return;
            }
        }

        openModalWithContent(
            'confirmation',
            <ConfirmationOverlay
                title="Confirmation"
                description={
                    activeBusinessPartner.amnisLegalEntityCountry
                        .split('/')
                        .pop() === 'CH'
                        ? 'It is a criminal offence to deliberately provide false information on this form (Article 251 of the Swiss Criminal Code, document forgery). Any changes must be immediately notified to the person subject to due diligence.'
                        : 'Deliberately providing false information on this form constitutes a criminal offence under the Liechtenstein Criminal Code (Strafgesetzbuch). Any changes must be immediately notified to the person subject to due diligence.'
                }
                onConfirm={() => handleConfirm()}
                onCancel={() => closeModal('confirmation')}
            />,
        );
    };

    const handleConfirm = () => {
        const self = crpList.find(s => s.crp.user === user['@id']);
        if (!self || (!self.isOwner && !self.hasSigningRights)) {
            openModalWithContent(
                'confirmation',
                <OnboardingOverlay hideCloseButton>
                    <ConfirmationModal
                        title="Warning"
                        description="You didn't select yourself for any of the beneficial owner(s) or persons, who will sign the contract. Is this right?"
                        onClose={() => closeModal('confirmation')}
                        onSubmit={() => {
                            closeModal('confirmation');
                            handleSubmit();
                        }}
                    />
                </OnboardingOverlay>,
            );
        } else {
            handleSubmit();
        }
    };

    const handleSubmit = () => {
        closeModal();
        onSuccess();
        queryClient.invalidateQueries({queryKey: [USER_CACHE]});
    };

    const getStepTitle = () => {
        if (inReview)
            return 'Has there been a change in the controlling person(s)?';
        if (isSinglePerson)
            return 'Declaration of identity of the beneficial owner';

        return 'Declaration of roles of people related to the business';
    };

    return (
        <>
            <div className={styles.form}>
                <StepTitle title={getStepTitle()} info={<CrpInfo />} />
            </div>

            <CrpTable
                crpList={crpList}
                isCrpLoading={isCrpLoading}
                currentStep={currentStep}
                legalType={legalType}
                inReview={inReview}
                formOfControl={activeBusinessPartner.gwgInfo?.formOfControl}
            />
            <div className={styles.form}>
                <NextStepButton onClick={handleFormSubmit} disabled={false} />
            </div>
        </>
    );
};

export default Crp;
